import * as React from 'react';
import { createRoute, Link, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { ChevronRight, FileText, Search } from 'lucide-react';
import type { DocType, DocumentStatus, DocumentSummary } from '@bits/shared/zod';
import { formatInr, paise } from '@bits/domain';
import { rootRoute } from './__root';
import { AppShell } from '@/components/AppShell';
import { api } from '@/lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { SortableTh } from '@/components/ui/sortable-th';
import { useSortedRows, type SortAccessors } from '@/lib/sort';

export const billingDocumentsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/billing/documents',
  component: BillingDocumentsPage,
});

const STATUS_FILTERS: ReadonlyArray<{ value: DocumentStatus | ''; label: string }> = [
  { value: '', label: 'All' },
  { value: 'draft', label: 'Draft' },
  { value: 'issued', label: 'Issued' },
  { value: 'partially_paid', label: 'Partially paid' },
  { value: 'paid', label: 'Paid' },
  { value: 'written_off', label: 'Written off' },
];

type DocumentSortKey = 'document' | 'client' | 'issued' | 'due' | 'total' | 'balance' | 'status';

const DOC_TYPE_LABEL: Record<DocType, string> = {
  tax_invoice: 'Tax invoice',
  bill_of_supply: 'Bill of supply',
  credit_note: 'Credit note',
  receipt: 'Receipt',
};

const STATUS_STYLE: Record<DocumentStatus, { label: string; dot: string; pill: string }> = {
  draft: {
    label: 'Draft',
    dot: 'bg-[hsl(var(--muted-foreground))]',
    pill: 'bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))]',
  },
  issued: {
    label: 'Issued',
    dot: 'bg-[hsl(var(--brand))]',
    pill: 'bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))]',
  },
  partially_paid: {
    label: 'Partially paid',
    dot: 'bg-[hsl(var(--brand))]/50',
    pill: 'bg-[hsl(var(--secondary))] text-[hsl(var(--secondary-foreground))]',
  },
  paid: {
    label: 'Paid',
    dot: 'bg-white/80',
    pill: 'bg-[hsl(var(--brand))] text-[hsl(var(--brand-foreground))]',
  },
  written_off: {
    label: 'Written off',
    dot: 'bg-[hsl(var(--destructive))]',
    pill: 'bg-[hsl(var(--destructive))]/10 text-[hsl(var(--destructive))]',
  },
};

function StatusPill({ status }: { status: DocumentStatus }): JSX.Element {
  const style = STATUS_STYLE[status];
  return (
    <span
      className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-medium ${style.pill}`}
    >
      <span aria-hidden className={`size-1.5 rounded-full ${style.dot}`} />
      {style.label}
    </span>
  );
}

const DATE_FMT = new Intl.DateTimeFormat('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });

function formatDate(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  if (!y || !m || !d) return iso;
  return DATE_FMT.format(new Date(Date.UTC(y, m - 1, d)));
}

function todayIso(): string {
  return new Date(Date.now() + (5 * 60 + 30) * 60 * 1000).toISOString().slice(0, 10);
}

function daysBetween(fromIso: string, toIso: string): number {
  return Math.round((Date.parse(toIso) - Date.parse(fromIso)) / 86_400_000);
}

function isOpen(d: DocumentSummary): boolean {
  return d.status === 'issued' || d.status === 'partially_paid';
}

function isOverdue(d: DocumentSummary, today: string): boolean {
  return isOpen(d) && d.dueDate !== null && d.dueDate < today && d.amountDuePaise > 0;
}

function DueCell({ doc, today }: { doc: DocumentSummary; today: string }): JSX.Element {
  if (!doc.dueDate) return <span className="text-[hsl(var(--muted-foreground))]">—</span>;
  const days = daysBetween(today, doc.dueDate);
  const note = !isOpen(doc)
    ? null
    : days < 0
      ? { text: `${-days} ${-days === 1 ? 'day' : 'days'} overdue`, tone: 'text-[hsl(var(--destructive))]' }
      : days === 0
        ? { text: 'Due today', tone: 'text-[hsl(var(--brand))]' }
        : { text: `in ${days} ${days === 1 ? 'day' : 'days'}`, tone: 'text-[hsl(var(--muted-foreground))]' };
  return (
    <div className="flex flex-col leading-tight">
      <span className="tabular-nums">{formatDate(doc.dueDate)}</span>
      {note ? <span className={`text-xs ${note.tone}`}>{note.text}</span> : null}
    </div>
  );
}

function StatTile({
  label,
  value,
  hint,
  tone = 'default',
}: {
  label: string;
  value: string;
  hint: string;
  tone?: 'default' | 'brand' | 'destructive';
}): JSX.Element {
  const valueTone =
    tone === 'brand'
      ? 'text-[hsl(var(--brand))]'
      : tone === 'destructive'
        ? 'text-[hsl(var(--destructive))]'
        : 'text-[hsl(var(--foreground))]';
  return (
    <div className="flex flex-col gap-1 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] px-4 py-3.5">
      <span className="text-[11px] font-semibold uppercase tracking-[0.08em] text-[hsl(var(--muted-foreground))]">
        {label}
      </span>
      <span className={`font-display text-2xl font-semibold tabular-nums tracking-tight ${valueTone}`}>
        {value}
      </span>
      <span className="text-xs text-[hsl(var(--muted-foreground))]">{hint}</span>
    </div>
  );
}

function sumPaise(docs: readonly DocumentSummary[], pick: (d: DocumentSummary) => number): bigint {
  let total = 0n;
  for (const d of docs) total += BigInt(pick(d));
  return total;
}

function BillingDocumentsPage(): JSX.Element {
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = React.useState<DocumentStatus | ''>('');
  const [docTypeFilter, setDocTypeFilter] = React.useState<DocType | ''>('');
  const [clientFilter, setClientFilter] = React.useState<string>('');
  const [search, setSearch] = React.useState('');
  const today = React.useMemo(todayIso, []);

  const listQuery = useQuery({
    queryKey: ['documents', statusFilter, docTypeFilter, clientFilter],
    queryFn: () =>
      api.listDocuments({
        status: (statusFilter || undefined) as DocumentStatus | undefined,
        docType: (docTypeFilter || undefined) as DocType | undefined,
        clientId: clientFilter || undefined,
        pageSize: 200,
      }),
  });
  const allQuery = useQuery({
    queryKey: ['documents', '', '', ''],
    queryFn: () => api.listDocuments({ pageSize: 200 }),
  });
  const clientsQuery = useQuery({
    queryKey: ['clients-for-billing'],
    queryFn: () => api.listClients({ status: 'active', pageSize: 100 }),
  });
  const projectsQuery = useQuery({
    queryKey: ['projects-for-billing'],
    queryFn: () => api.listProjects({}),
  });

  const clients = clientsQuery.data?.items ?? [];
  const projects = projectsQuery.data ?? [];
  const clientById = React.useMemo(() => new Map(clients.map((c) => [c.id, c])), [clients]);
  const projectById = React.useMemo(() => new Map(projects.map((p) => [p.id, p])), [projects]);

  const clientName = React.useCallback(
    (id: string): string => clientById.get(id)?.displayName ?? id,
    [clientById],
  );

  const visible = React.useMemo(() => {
    const items = listQuery.data?.items ?? [];
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter((d) => {
      const number = (d.number ?? d.requestedNumber ?? '').toLowerCase();
      return number.includes(q) || clientName(d.clientId).toLowerCase().includes(q);
    });
  }, [listQuery.data, search, clientName]);

  const sortAccessors = React.useMemo<SortAccessors<DocumentSummary, DocumentSortKey>>(
    () => ({
      document: (d) => d.number ?? d.requestedNumber ?? null,
      client: (d) => clientName(d.clientId),
      issued: (d) => d.issueDate,
      due: (d) => d.dueDate,
      total: (d) => d.totalPaise,
      balance: (d) => (d.status === 'draft' ? null : d.amountDuePaise),
      status: (d) => STATUS_STYLE[d.status].label,
    }),
    [clientName],
  );
  const { rows, ...sorter } = useSortedRows(visible, sortAccessors);

  const stats = React.useMemo(() => {
    const all = allQuery.data?.items ?? [];
    const open = all.filter(isOpen);
    const overdue = all.filter((d) => isOverdue(d, today));
    const drafts = all.filter((d) => d.status === 'draft');
    const paid = all.filter((d) => d.status === 'paid');
    return {
      outstanding: sumPaise(open, (d) => d.amountDuePaise),
      openCount: open.length,
      overdue: sumPaise(overdue, (d) => d.amountDuePaise),
      overdueCount: overdue.length,
      drafts: drafts.length,
      draftsValue: sumPaise(drafts, (d) => d.totalPaise),
      collected: sumPaise(paid, (d) => d.totalPaise),
      paidCount: paid.length,
    };
  }, [allQuery.data, today]);

  const total = listQuery.data?.total ?? 0;
  const filtered = Boolean(statusFilter || docTypeFilter || clientFilter || search.trim());
  const plural = (n: number, one: string, many: string): string => `${n} ${n === 1 ? one : many}`;

  const clearFilters = (): void => {
    setStatusFilter('');
    setDocTypeFilter('');
    setClientFilter('');
    setSearch('');
  };

  return (
    <AppShell>
      <div className="flex flex-wrap items-start justify-between gap-4 lg:flex-nowrap">
        <div className="flex min-w-0 flex-col gap-2">
          <h1 className="font-display text-4xl font-semibold tracking-tight md:text-5xl">
            Documents
          </h1>
          <p className="text-base text-[hsl(var(--muted-foreground))]">
            Tax invoices, bills of supply, credit notes and receipts. A number is assigned the
            moment a document is issued.
          </p>
        </div>
        <div className="flex shrink-0 gap-2">
          <Button onClick={() => void navigate({ to: '/billing/documents/new' })}>
            New invoice
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 xl:grid-cols-4">
        <StatTile
          label="Outstanding"
          value={formatInr(paise(Number(stats.outstanding)))}
          hint={plural(stats.openCount, 'open document', 'open documents')}
          tone="brand"
        />
        <StatTile
          label="Overdue"
          value={formatInr(paise(Number(stats.overdue)))}
          hint={
            stats.overdueCount === 0
              ? 'Nothing past its due date'
              : plural(stats.overdueCount, 'document past due', 'documents past due')
          }
          tone={stats.overdueCount > 0 ? 'destructive' : 'default'}
        />
        <StatTile
          label="Collected"
          value={formatInr(paise(Number(stats.collected)))}
          hint={plural(stats.paidCount, 'document paid in full', 'documents paid in full')}
        />
        <StatTile
          label="Drafts"
          value={String(stats.drafts)}
          hint={
            stats.drafts === 0
              ? 'No drafts waiting'
              : `${formatInr(paise(Number(stats.draftsValue)))} not yet issued`
          }
        />
      </div>

      <div className="flex flex-col gap-3">
        <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
          <div className="relative w-full sm:max-w-xs sm:flex-1">
            <Search
              aria-hidden
              className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-[hsl(var(--muted-foreground))]"
            />
            <Input
              className="pl-9"
              placeholder="Search number or client…"
              value={search}
              onChange={(e) => setSearch(e.currentTarget.value)}
            />
          </div>
          <Select
            aria-label="Type"
            className="sm:w-44"
            value={docTypeFilter}
            onChange={(e) => setDocTypeFilter(e.currentTarget.value as DocType | '')}
          >
            <option value="">All types</option>
            {(Object.keys(DOC_TYPE_LABEL) as DocType[]).map((t) => (
              <option key={t} value={t}>
                {DOC_TYPE_LABEL[t]}
              </option>
            ))}
          </Select>
          <Select
            aria-label="Client"
            className="sm:w-56"
            value={clientFilter}
            onChange={(e) => setClientFilter(e.currentTarget.value)}
          >
            <option value="">All clients</option>
            {clients.map((c) => (
              <option key={c.id} value={c.id}>
                {c.displayName}
              </option>
            ))}
          </Select>
        </div>
        <div className="-mx-1 overflow-x-auto px-1">
          <div className="flex w-max self-start rounded-xl border border-[hsl(var(--border))] p-0.5">
            {STATUS_FILTERS.map((s) => (
              <button
                key={s.value}
                type="button"
                onClick={() => setStatusFilter(s.value)}
                className={
                  'whitespace-nowrap rounded-lg px-3 py-1.5 text-sm transition-colors ' +
                  (statusFilter === s.value
                    ? 'bg-[hsl(var(--brand))] text-white'
                    : 'text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]')
                }
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <Card className="overflow-hidden">
        <CardHeader className="flex-row items-end justify-between gap-4 space-y-0">
          <div className="flex flex-col gap-1">
            <CardTitle>
              {statusFilter ? STATUS_FILTERS.find((s) => s.value === statusFilter)?.label : 'All'}{' '}
              documents
            </CardTitle>
            <CardDescription>
              {rows.length === total
                ? plural(total, 'document', 'documents')
                : `Showing ${rows.length} of ${total}`}
            </CardDescription>
          </div>
          {listQuery.isFetching ? (
            <span className="text-xs text-[hsl(var(--muted-foreground))]">Refreshing…</span>
          ) : null}
        </CardHeader>
        <CardContent className="p-0">
          {listQuery.isPending ? (
            <p className="px-6 py-10 text-center text-sm text-[hsl(var(--muted-foreground))]">
              Loading…
            </p>
          ) : rows.length === 0 ? (
            <div className="flex flex-col items-center gap-3 px-6 py-14 text-center">
              <div className="flex size-12 items-center justify-center rounded-full bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))]">
                <FileText aria-hidden className="size-5" />
              </div>
              <p className="text-sm text-[hsl(var(--muted-foreground))]">
                {filtered
                  ? 'No documents match these filters.'
                  : 'No documents yet. Create an invoice, then issue it to assign a number.'}
              </p>
              {filtered ? (
                <Button variant="outline" size="sm" onClick={clearFilters}>
                  Clear filters
                </Button>
              ) : (
                <Button size="sm" onClick={() => void navigate({ to: '/billing/documents/new' })}>
                  New invoice
                </Button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[880px] text-sm">
                <thead>
                  <tr className="border-y border-[hsl(var(--border))] bg-[hsl(var(--muted))] text-left text-[11px] font-semibold uppercase tracking-[0.08em] text-[hsl(var(--muted-foreground))]">
                    <SortableTh sortKey="document" controller={sorter} className="py-2.5 pl-6 pr-3">
                      Document
                    </SortableTh>
                    <SortableTh sortKey="client" controller={sorter} className="py-2.5 pr-3">
                      Client
                    </SortableTh>
                    <SortableTh sortKey="issued" controller={sorter} className="py-2.5 pr-3">
                      Issued
                    </SortableTh>
                    <SortableTh sortKey="due" controller={sorter} className="py-2.5 pr-3">
                      Due
                    </SortableTh>
                    <SortableTh sortKey="total" controller={sorter} align="right" className="py-2.5 pr-3">
                      Total
                    </SortableTh>
                    <SortableTh sortKey="balance" controller={sorter} align="right" className="py-2.5 pr-3">
                      Balance
                    </SortableTh>
                    <SortableTh sortKey="status" controller={sorter} className="py-2.5 pr-3">
                      Status
                    </SortableTh>
                    <th className="w-12 py-2.5 pr-4" aria-label="Open" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-[hsl(var(--border))]">
                  {rows.map((d) => {
                    const project = d.projectId ? projectById.get(d.projectId) : null;
                    const overdue = isOverdue(d, today);
                    return (
                      <tr
                        key={d.id}
                        onClick={() =>
                          void navigate({
                            to: '/billing/documents/$documentId',
                            params: { documentId: d.id },
                          })
                        }
                        className="group cursor-pointer transition-colors hover:bg-[hsl(var(--accent))]/60"
                      >
                        <td className="py-3 pl-6 pr-3">
                          <div className="flex min-w-0 flex-col gap-0.5">
                            <Link
                              to="/billing/documents/$documentId"
                              params={{ documentId: d.id }}
                              onClick={(e) => e.stopPropagation()}
                              className="font-mono text-[13px] font-semibold tracking-wide text-[hsl(var(--foreground))] group-hover:text-[hsl(var(--brand))]"
                            >
                              {d.number ?? d.requestedNumber ?? 'Draft'}
                            </Link>
                            <span className="text-xs text-[hsl(var(--muted-foreground))]">
                              {DOC_TYPE_LABEL[d.docType]}
                              {d.status === 'draft' && d.requestedNumber ? ' · number reserved' : ''}
                              {project ? ` · ${project.code}` : ''}
                            </span>
                          </div>
                        </td>
                        <td className="py-3 pr-3">
                          <span className="block max-w-[220px] truncate font-medium">
                            {clientName(d.clientId)}
                          </span>
                        </td>
                        <td className="py-3 pr-3 tabular-nums">{formatDate(d.issueDate)}</td>
                        <td className="py-3 pr-3">
                          <DueCell doc={d} today={today} />
                        </td>
                        <td className="py-3 pr-3 text-right tabular-nums">
                          {formatInr(paise(d.totalPaise))}
                        </td>
                        <td
                          className={`py-3 pr-3 text-right tabular-nums ${
                            overdue
                              ? 'font-semibold text-[hsl(var(--destructive))]'
                              : d.amountDuePaise === 0 || d.status === 'draft'
                                ? 'text-[hsl(var(--muted-foreground))]'
                                : 'font-medium'
                          }`}
                        >
                          {d.status === 'draft'
                            ? '—'
                            : d.amountDuePaise === 0
                              ? 'Settled'
                              : formatInr(paise(d.amountDuePaise))}
                        </td>
                        <td className="py-3 pr-3">
                          <StatusPill status={d.status} />
                        </td>
                        <td className="py-3 pr-4 text-right">
                          <ChevronRight
                            aria-hidden
                            className="ml-auto size-4 text-[hsl(var(--muted-foreground))] opacity-0 transition-opacity group-hover:opacity-100"
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </AppShell>
  );
}
